import type { TermsTemplate } from '@/hooks/settings/useTermsTemplates';

/**
 * How a record picked its Terms & Conditions.
 *
 *   DEFAULT  — whatever the tenant's default repository entry says
 *   TEMPLATE — a specific repository entry, pinned by id
 *   CUSTOM   — one-off text typed on the record itself
 *
 * Mirrors `TERMS_MODES` in operations. Job Types and Estimate Templates store a
 * selection only; Jobs and Estimates store the selection *and* a snapshot of the
 * resolved text, so editing a template never rewrites a document already sent.
 */
export const TERMS_MODES = ['DEFAULT', 'TEMPLATE', 'CUSTOM'] as const;

export type TermsMode = (typeof TERMS_MODES)[number];

export interface TermsSelection {
  termsMode: TermsMode;
  /** Ordered — the stacked document reads in exactly this sequence. */
  termsTemplateIds: string[];
  /** For DEFAULT/TEMPLATE this is the snapshot to display, not to edit. */
  termsAndConditions: string;
}

/** The three columns as they arrive off an API record (any of them nullable). */
export interface TermsSelectionRecord {
  termsMode?: string | null;
  termsTemplateIds?: readonly string[] | null;
  termsAndConditions?: string | null;
}

export const EMPTY_TERMS_SELECTION: TermsSelection = {
  termsMode: 'DEFAULT',
  termsTemplateIds: [],
  termsAndConditions: '',
};

/** Mirrors `TERMS_JOIN` on the backend: a blank line between stacked entries. */
export const TERMS_JOIN = '\n\n';

/** Stack entry wording in order, skipping blanks. */
export function joinTermsContent(parts: readonly (string | null | undefined)[]): string {
  return parts
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(TERMS_JOIN);
}

export function isTermsMode(value: unknown): value is TermsMode {
  return typeof value === 'string' && (TERMS_MODES as readonly string[]).includes(value);
}

export function findDefaultTermsTemplate(
  templates: readonly TermsTemplate[],
): TermsTemplate | null {
  return templates.find((template) => template.isDefault) ?? null;
}

/**
 * Did this record express a choice? A bare `DEFAULT` counts — picking "use the
 * default" on a job type is a decision, and it stops the inheritance walk there.
 */
export function hasTermsSelection(record: TermsSelectionRecord | null | undefined): boolean {
  if (!record) return false;
  if (isTermsMode(record.termsMode)) return true;
  if (record.termsAndConditions?.trim()) return true;
  // A pinned stack with no mode is still a choice — mirrors the backend's
  // selectionMode rule, so a row the server reads as TEMPLATE cannot be read
  // as "nothing chosen" here and inherited past.
  return (record.termsTemplateIds?.length ?? 0) > 0;
}

/**
 * Read a saved document (estimate/job) into editable form state. Documents carry
 * their own snapshot, so this needs no repository lookup — what it shows is
 * exactly what was stored.
 */
export function readTermsSelection(
  record: TermsSelectionRecord | null | undefined,
): TermsSelection {
  const content = record?.termsAndConditions ?? '';
  const mode: TermsMode = isTermsMode(record?.termsMode)
    ? record.termsMode
    : // Text with no mode predates the repository (or came from an API client
      // that only sends text) — that text is custom. A pinned stack with no
      // mode and no text is a TEMPLATE choice, mirroring the backend: reading
      // it as DEFAULT would show tenant-default terms and, on save, post them
      // back — silently clearing the original pin.
      content.trim()
      ? 'CUSTOM'
      : (record?.termsTemplateIds?.length ?? 0) > 0
        ? 'TEMPLATE'
        : 'DEFAULT';

  return {
    termsMode: mode,
    termsTemplateIds: mode === 'CUSTOM' ? [] : [...(record?.termsTemplateIds ?? [])],
    termsAndConditions: content,
  };
}

/**
 * Resolve a selection against the repository, filling in the text to display.
 * Used when seeding a NEW document from a configuration holder (job type,
 * estimate template) or from the tenant default — those store a selection with
 * no snapshot. Mirrors `TermsResolutionService.resolve` on the backend.
 */
export function resolveTermsSelection(
  record: TermsSelectionRecord | null | undefined,
  templates: readonly TermsTemplate[],
): TermsSelection {
  const selection = readTermsSelection(record);

  if (selection.termsMode === 'CUSTOM') return selection;

  if (selection.termsMode === 'TEMPLATE') {
    // Entries that no longer exist (deleted server-side) drop out of the
    // stack; the survivors keep their order. Only a stack with nothing left
    // degrades to the default.
    const pinned = selection.termsTemplateIds
      .map((id) => templates.find((t) => t.id === id))
      .filter((t): t is TermsTemplate => Boolean(t));
    if (pinned.length > 0) {
      return {
        termsMode: 'TEMPLATE',
        termsTemplateIds: pinned.map((t) => t.id),
        termsAndConditions: joinTermsContent(pinned.map((t) => t.content)),
      };
    }
  }

  const fallback = findDefaultTermsTemplate(templates);
  return {
    termsMode: 'DEFAULT',
    termsTemplateIds: fallback ? [fallback.id] : [],
    // Keep any snapshot already on the record when the tenant has no default —
    // clearing it would silently drop terms the customer already saw.
    termsAndConditions: fallback?.content ?? selection.termsAndConditions,
  };
}

/**
 * Walk an inheritance chain, most specific source first, and resolve the first
 * one that expressed a choice. Falls back to the tenant default.
 *
 * Estimate: the parent job → the estimate template → the tenant default.
 * Job:      the source estimate → the job type → the tenant default.
 */
export function inheritTermsSelection(
  sources: readonly (TermsSelectionRecord | null | undefined)[],
  templates: readonly TermsTemplate[],
): TermsSelection {
  const chosen = sources.find(hasTermsSelection);
  return resolveTermsSelection(chosen ?? null, templates);
}

/**
 * The payload shape the operations DTOs accept. Only CUSTOM sends text — for the
 * other modes the server re-reads the repository, so sending the preview would
 * be ignored at best and stale at worst.
 */
export function toTermsPayload(selection: TermsSelection): {
  termsMode: TermsMode;
  termsTemplateIds: string[];
  termsAndConditions: string;
} {
  if (selection.termsMode === 'CUSTOM') {
    return {
      termsMode: 'CUSTOM',
      termsTemplateIds: [],
      termsAndConditions: selection.termsAndConditions.trim(),
    };
  }
  if (selection.termsMode === 'TEMPLATE') {
    if (selection.termsTemplateIds.length > 0) {
      return {
        termsMode: 'TEMPLATE',
        termsTemplateIds: selection.termsTemplateIds,
        termsAndConditions: '',
      };
    }
    // Every pinned entry has since been deleted. The text the customer saw is
    // still on the record and outranks the repository, so it is saved as CUSTOM
    // rather than collapsed to DEFAULT — collapsing would let an unrelated edit
    // (changing only the notes) silently swap in the current tenant default.
    // Mirrors TermsResolutionService.resolve().
    if (selection.termsAndConditions.trim()) {
      return {
        termsMode: 'CUSTOM',
        termsTemplateIds: [],
        termsAndConditions: selection.termsAndConditions.trim(),
      };
    }
  }
  return { termsMode: 'DEFAULT', termsTemplateIds: [], termsAndConditions: '' };
}

/**
 * The terms fields a *preview* document carries — the estimate form renders a
 * PDF of what has not been saved yet.
 *
 * {@link toTermsPayload} must never drive that preview: it deliberately sends no
 * text for DEFAULT/TEMPLATE because the server re-reads the repository on save,
 * so previewing it printed an empty "Terms & Conditions" section for every
 * document using the tenant default or a pinned template — while the form, the
 * saved estimate and the customer's copy all showed the terms. A preview shows
 * what the customer will read, which is the snapshot the selector resolved.
 */
export function toTermsPreviewFields(selection: TermsSelection): {
  termsMode: TermsMode;
  termsTemplateIds: string[];
  termsAndConditions: string | null;
} {
  return {
    termsMode: selection.termsMode,
    termsTemplateIds: selection.termsMode === 'CUSTOM' ? [] : selection.termsTemplateIds,
    termsAndConditions: selection.termsAndConditions.trim() || null,
  };
}

/** Human label for a stored selection — used in read-only detail views. */
export function describeTermsSelection(
  record: TermsSelectionRecord | null | undefined,
  templates: readonly TermsTemplate[],
): string {
  const mode = isTermsMode(record?.termsMode) ? record.termsMode : null;
  if (mode === 'CUSTOM') return 'Custom';
  if (mode === 'TEMPLATE') {
    const names = (record?.termsTemplateIds ?? [])
      .map((id) => templates.find((t) => t.id === id)?.name)
      .filter(Boolean);
    if (names.length === 0) return 'Template (removed)';
    // All names, in stack order — "General + Warranty" reads as the document does.
    return names.join(' + ');
  }
  const fallback = findDefaultTermsTemplate(templates);
  return fallback ? `Default — ${fallback.name}` : 'Default';
}

/**
 * The payload shape for records whose snapshot column is `terms`, not
 * `termsAndConditions` — invoices today, proposals next. Same rules as
 * {@link toTermsPayload}; only the key differs, and it differs in exactly one
 * place on each side of the wire (`toTermsColumn` in operations).
 */
export function toTermsColumnPayload(selection: TermsSelection): {
  termsMode: TermsMode;
  termsTemplateIds: string[];
  terms: string;
} {
  const { termsAndConditions, ...rest } = toTermsPayload(selection);
  return { ...rest, terms: termsAndConditions };
}

/**
 * What a NEW document starts with, terms-wise: the parent document's wording
 * when it has any, otherwise the tenant's default repository entry.
 *
 * The parent comes first for the reason the server inherits from it — the
 * customer is already working under that wording, and a document quoting the
 * *current* tenant default would disagree with what they accepted. The form has
 * to make this decision rather than leave it to the server: it always sends an
 * explicit selection, so the server's inheritance branch never runs for a form
 * create, and a blank selector would then save terms the operator never saw.
 *
 * Parent is the job for an invoice, the source service-call job for a proposal.
 * Returns null when there is nothing to seed.
 */
export function seedDocumentTerms(
  parent: TermsSelectionRecord | null | undefined,
  templates: readonly TermsTemplate[],
): TermsSelection | null {
  // A parent that carries WORDING is used verbatim — that is the text the
  // customer is already working under, and re-resolving it would swap in
  // whatever the repository says today.
  const fromParent = hasTermsSelection(parent) ? readTermsSelection(parent) : null;
  if (fromParent?.termsAndConditions.trim()) return fromParent;

  // A parent with a selection but NO snapshot is re-resolved, exactly as
  // `inheritFromDocument` does on the server: a pinned template resolves to that
  // template's current wording, and anything else to the tenant default.
  // `resolveTermsSelection` is the existing mirror of the server's `resolve`, so
  // this defers to it rather than restating the rules and drifting from them —
  // which is precisely what a hand-rolled version of this did.
  const resolved = resolveTermsSelection(fromParent, templates);
  return resolved.termsAndConditions.trim() ? resolved : null;
}
